import { useState, useRef, useEffect } from 'react';
import { CaretDown, Plus, DotsThreeVertical } from '@phosphor-icons/react';
import { BounceLoader, MoonLoader } from 'react-spinners';
import { PromptSelectorProps, PromptVersionControl } from '@Models/types';
import colors from '@Common/assets/colors';

const PromptSelector = ({
  prompts,
  selectedPrompt,
  loadingPrompts,
  onPromptSelect,
  onCreatePrompt,
  onViewPrompt,
  onEditPrompt,
  onDeletePrompt,
}: PromptSelectorProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [openActionMenu, setOpenActionMenu] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    right: number;
  } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<{ [key: number]: HTMLButtonElement | null }>({});

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target as Node)
      ) {
        setOpenActionMenu(null);
        setMenuPosition(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handlePromptSelect = (prompt: PromptVersionControl) => {
    onPromptSelect(prompt);
    setIsDropdownOpen(false);
  };

  const handleCreatePrompt = () => {
    setIsDropdownOpen(false);
    onCreatePrompt();
  };

  const handleActionMenuClick = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();

    if (openActionMenu === index) {
      setOpenActionMenu(null);
      setMenuPosition(null);
    } else {
      const button = buttonRefs.current[index];
      const dropdown = dropdownRef.current;

      if (button && dropdown) {
        const buttonRect = button.getBoundingClientRect();
        const dropdownRect = dropdown.getBoundingClientRect();

        const top = buttonRect.top - dropdownRect.top;
        const right = 10;

        setMenuPosition({ top, right });
        setOpenActionMenu(index);
      }
    }
  };

  const placeholder =
    loadingPrompts && !selectedPrompt
      ? 'Loading Prompts...'
      : selectedPrompt
        ? `Version ${selectedPrompt.version}${selectedPrompt.nickname ? ` - ${selectedPrompt.nickname}` : ''}`
        : 'Select Prompt Version';

  return (
    <div className="flex flex-col gap-y-5">
      <span className="font-extrabold text-primary-700">Prompts</span>

      <div ref={dropdownRef} className="relative w-full">
        <div
          className="flex cursor-pointer items-center justify-between rounded-md border border-gray-300 px-4 py-2"
          onClick={() => !loadingPrompts && setIsDropdownOpen(!isDropdownOpen)}
        >
          <span className="font-inter text-sm text-neutral-600">
            {placeholder}
          </span>
          {loadingPrompts ? (
            <BounceLoader
              data-testid="Loader"
              size={20}
              color={colors.brandWarning[500]}
            />
          ) : (
            <CaretDown
              size={16}
              className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
            />
          )}
        </div>

        {isDropdownOpen && (
          <div className="absolute top-full z-50 mt-1 w-full rounded-md border border-gray-300 bg-white shadow-lg">
            <div className="border-b border-gray-200 bg-gray-50">
              <button
                onClick={handleCreatePrompt}
                className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium text-primary-700 hover:bg-primary-50"
              >
                <Plus size={16} />
                Create New Prompt
              </button>
            </div>

            <div className="max-h-60 overflow-y-auto">
              <ul>
                {loadingPrompts ? (
                  <li className="flex h-full w-full flex-col items-center justify-center gap-y-4 p-5">
                    <MoonLoader size={50} color="#071D49" />
                    <span className="animate-pulse text-xs font-semibold tracking-wide text-gray-600">
                      Prompts Loading
                    </span>
                  </li>
                ) : prompts.length === 0 ? (
                  <li className="px-4 py-2 text-gray-500">No prompts found</li>
                ) : (
                  prompts.map((prompt, index) => (
                    <li
                      key={`prompt-${index}`}
                      className={`flex items-center justify-between border-b border-gray-100 px-4 py-2 hover:bg-gray-50 ${
                        selectedPrompt?.version === prompt.version
                          ? 'bg-primary-50'
                          : ''
                      }`}
                    >
                      <div
                        onClick={() => handlePromptSelect(prompt)}
                        className="flex-1 cursor-pointer"
                      >
                        <div className="text-sm font-medium">
                          Version {prompt.version}
                          {prompt.nickname && (
                            <span className="text-gray-600">
                              {' '}
                              - {prompt.nickname}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          by {prompt.created_by.name} on {prompt.date}
                          {prompt.is_personal !== undefined &&
                            !prompt.is_personal && (
                              <span className="ml-2 rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-600">
                                Global
                              </span>
                            )}
                        </div>
                      </div>

                      <div className="relative">
                        <button
                          ref={(el) => (buttonRefs.current[index] = el)}
                          onClick={(e) => handleActionMenuClick(index, e)}
                          className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-200"
                          title="Actions"
                        >
                          <DotsThreeVertical size={16} />
                        </button>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </div>

            {/* Action menu rendered outside scrollable container */}
            {openActionMenu !== null && menuPosition && (
              <div
                ref={actionMenuRef}
                className="absolute z-[60] w-24 rounded-md border border-gray-300 bg-white shadow-lg"
                style={{
                  top: `${menuPosition.top}px`,
                  right: `${menuPosition.right}px`,
                }}
                onMouseLeave={() => {
                  setOpenActionMenu(null);
                }}
              >
                <ul className="py-1">
                  <li>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenActionMenu(null);
                        setMenuPosition(null);
                        setIsDropdownOpen(false);
                        onViewPrompt(prompts[openActionMenu]);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
                    >
                      View
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenActionMenu(null);
                        setMenuPosition(null);
                        setIsDropdownOpen(false);
                        onEditPrompt(prompts[openActionMenu]);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
                    >
                      Edit
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenActionMenu(null);
                        setMenuPosition(null);
                        setIsDropdownOpen(false);
                        onDeletePrompt(prompts[openActionMenu]);
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-gray-100"
                    >
                      Delete
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptSelector;
